// A new library asset from its first picture. Used by the asset wizard, the server and scripts/cast.ts.
import { NATURAL } from "../audio/voice/voice";
import type { Picture } from "../model/episode";
import type { FaceSpots } from "../model/face";
import { uid } from "../model/ids";
import type { Asset, AssetKind, LookVersion } from "../model/library";
import { DEFAULT_SPEECH } from "../model/speech";

export const COLORS = ["#3FB8A8", "#FF7A59", "#7B6CF6", "#4C9BF0", "#F25C9A", "#8BC34A", "#FFD23F", "#2E3A59"];

// What the painter sends back: the picture, and for characters where the face goes on it.
export type Painted = Picture & { face?: FaceSpots };

export const lookOf = ({ face, ...picture }: Painted): Pick<Asset, "picture" | "face"> => (face ? { picture, face } : { picture });

const versionOf = (painted: Painted, how: LookVersion["how"], text: string, at = Date.now()): LookVersion => ({ id: uid("v"), ...lookOf(painted), how, text, at });

// Every look an asset has had. Assets made before versions existed have just the one they show now.
export const versionsOf = (a: Asset): LookVersion[] =>
  a.versions ?? (!a.picture.src ? [] : [{ id: "current", picture: a.picture, ...(a.face ? { face: a.face } : {}), how: "drawn", text: a.description, at: a.updatedAt }]);

// The picture and face that make a version the asset's look.
export const selectVersion = (v: LookVersion): Pick<Asset, "picture" | "face"> => ({ picture: v.picture, face: v.face });
export const isSelected = (a: Asset, v: LookVersion) => v.picture.src === a.picture.src;

// A new picture joins the versions; `select` also makes it the asset's look.
export const addVersion = (a: Asset, painted: Painted, how: LookVersion["how"], text: string, select: boolean): Asset => {
  const v = versionOf(painted, how, text);
  return { ...a, ...(select ? selectVersion(v) : {}), versions: [...versionsOf(a), v] };
};

// Takes one version away. If it was the asset's look, the newest one left becomes the look. The last version is never
// taken away.
export const removeVersion = (a: Asset, versionId: string): Asset => {
  const all = versionsOf(a);
  const gone = all.find(v => v.id === versionId);
  if (!gone || all.length < 2) return a;
  const left = all.filter(v => v.id !== versionId);
  return { ...a, ...(isSelected(a, gone) ? selectVersion(left[left.length - 1]!) : {}), versions: left };
};

// A hand-placed face goes on the version showing that picture too, so coming back to it keeps the face.
export const faceInVersions = (versions: LookVersion[] | undefined, src: string, face: FaceSpots) =>
  versions?.map(v => (v.picture.src === src ? { ...v, face } : v));

export function newAsset(kind: AssetKind, name: string, description: string, painted: Painted, color: string): Asset {
  const now = Date.now();
  return {
    id: uid(kind === "character" ? "c" : kind === "prop" ? "pr" : "bg"), kind, name, description, color, ...lookOf(painted), versions: [versionOf(painted, "drawn", description, now)],
    ...(kind === "character" ? { voiceMode: "record" as const, voice: NATURAL, speech: DEFAULT_SPEECH } : {}),
    createdAt: now, updatedAt: now,
  };
}

// A new asset that has no picture yet: it lives in the form until its first picture arrives (see drafts.ts).
export function blankAsset(kind: AssetKind, color: string): Asset {
  const now = Date.now();
  return {
    id: uid(kind === "character" ? "c" : kind === "prop" ? "pr" : "bg"), kind, name: "", description: "", color, picture: { src: "", w: 0, h: 0 },
    ...(kind === "character" ? { voiceMode: "record" as const, voice: NATURAL, speech: DEFAULT_SPEECH } : {}),
    createdAt: now, updatedAt: now,
  };
}
export const hasPicture = (a: Asset) => !!a.picture.src;
